import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Eye, Pencil, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  LEAD_SOURCES, LEAD_STATUSES, LEAD_PRIORITIES, statusMeta, priorityMeta, initials,
} from "@/config/leads";
import { LeadFormDialog } from "@/components/leads/LeadFormDialog";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function OwnerCell({ name, avatar }) {
  if (!name) return <span className="text-xs text-slate-400">Unassigned</span>;
  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-6 w-6"><AvatarImage src={avatar} /><AvatarFallback className="bg-emerald-100 text-[10px] text-emerald-700">{initials(name)}</AvatarFallback></Avatar>
      <span className="text-sm text-slate-700">{name}</span>
    </div>
  );
}

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState([]);

  const [search, setSearch] = useState("");
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [owner, setOwner] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    api.get("/leads/owners").then(({ data }) => setOwners(data.owners)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (source !== "all") params.source = source;
    if (status !== "all") params.status = status;
    if (priority !== "all") params.priority = priority;
    if (owner !== "all") params.owner = owner;
    try {
      const { data } = await api.get("/leads", { params });
      setLeads(data.leads);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [search, source, status, priority, owner]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const hotCount = leads.filter((l) => l.priority === "hot").length;
  const newCount = leads.filter((l) => l.status === "new").length;
  const hasFilters = source !== "all" || status !== "all" || priority !== "all" || owner !== "all" || search.trim();

  const clearFilters = () => {
    setSearch(""); setSource("all"); setStatus("all"); setPriority("all"); setOwner("all");
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Leads</h1>
          <p className="mt-1 text-sm text-slate-500">
            <span data-testid="leads-total-count" className="font-medium text-slate-700">{total}</span> total ·{" "}
            <span className="text-red-600">{hotCount} hot</span> ·{" "}
            <span className="text-sky-700">{newCount} new</span>
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-lead-button">
          <Plus className="h-4 w-4" /> Add Lead
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="leads-search-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:flex">
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="lg:w-[140px]" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Sources</SelectItem>{LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="lg:w-[150px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Statuses</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="lg:w-[130px]" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Priority</SelectItem>{LEAD_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger className="lg:w-[150px]" data-testid="filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Owners</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {hasFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-slate-500" data-testid="clear-filters">
              <X className="h-4 w-4" /> Clear
            </Button>
          )}
        </div>
      </Card>

      {/* Table (desktop) */}
      <Card className="hidden overflow-hidden shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="pl-6 text-xs uppercase tracking-wider text-slate-400">Lead</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Interest</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Source</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Priority</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Owner</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Created</TableHead>
              <TableHead className="pr-6 text-right text-xs uppercase tracking-wider text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={8} className="px-6"><Skeleton className="h-8 w-full" /></TableCell></TableRow>
              ))
            ) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-16 text-center text-sm text-slate-400" data-testid="leads-empty">No leads match your filters.</TableCell></TableRow>
            ) : (
              leads.map((lead) => {
                const st = statusMeta(lead.status);
                const pr = priorityMeta(lead.priority);
                return (
                  <TableRow
                    key={lead.id}
                    className="cursor-pointer border-slate-100"
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    data-testid="lead-row"
                  >
                    <TableCell className="pl-6">
                      <p className="font-medium text-slate-900">{lead.full_name}</p>
                      <p className="font-mono text-xs text-slate-400">{lead.phone}</p>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.interest}</TableCell>
                    <TableCell className="text-sm text-slate-600">{lead.source}</TableCell>
                    <TableCell><Badge className={cn(st.badge)}>{st.label}</Badge></TableCell>
                    <TableCell><Badge className={cn("capitalize", pr.badge)}>{pr.label}</Badge></TableCell>
                    <TableCell><OwnerCell name={lead.owner_name} avatar={lead.owner_avatar} /></TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{formatDate(lead.created_at)}</TableCell>
                    <TableCell className="pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}><Eye className="h-4 w-4" /> View</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}?edit=1`)}><Pencil className="h-4 w-4" /> Edit</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Cards (mobile) */}
      <div className="space-y-3 md:hidden">
        {loading ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
        ) : leads.length === 0 ? (
          <Card className="py-12 text-center text-sm text-slate-400 shadow-sm">No leads match your filters.</Card>
        ) : (
          leads.map((lead) => {
            const st = statusMeta(lead.status);
            const pr = priorityMeta(lead.priority);
            return (
              <Card key={lead.id} className="p-4 shadow-sm" onClick={() => navigate(`/leads/${lead.id}`)} data-testid="lead-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{lead.full_name}</p>
                    <p className="font-mono text-xs text-slate-400">{lead.phone}</p>
                  </div>
                  <Badge className={cn("capitalize", pr.badge)}>{pr.label}</Badge>
                </div>
                <p className="mt-2 text-sm text-slate-600">{lead.interest}</p>
                <div className="mt-3 flex items-center justify-between">
                  <Badge className={cn(st.badge)}>{st.label}</Badge>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Users className="h-3.5 w-3.5" /> {lead.owner_name || "Unassigned"} · {lead.source}
                  </span>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <LeadFormDialog open={dialogOpen} onOpenChange={setDialogOpen} lead={null} owners={owners} onSaved={load} />
    </div>
  );
}
